import { opposite, type Color } from '../primitives/color.js'
import {
  BISHOP,
  EMPTY,
  KING,
  KNIGHT,
  PAWN,
  QUEEN,
  ROOK,
  encodePiece,
  type PieceCode,
} from '../primitives/piece.js'
import { isOnBoard, type Square } from '../primitives/square.js'
import type { Position } from '../position/position.js'

export const NORTH = 16
export const SOUTH = -16
export const EAST = 1
export const WEST = -1
export const NORTH_EAST = 17
export const NORTH_WEST = 15
export const SOUTH_EAST = -15
export const SOUTH_WEST = -17

export const ROOK_DELTAS = [NORTH, SOUTH, EAST, WEST] as const
export const BISHOP_DELTAS = [NORTH_EAST, NORTH_WEST, SOUTH_EAST, SOUTH_WEST] as const
export const QUEEN_DELTAS = [...ROOK_DELTAS, ...BISHOP_DELTAS] as const
export const KING_DELTAS = QUEEN_DELTAS
export const KNIGHT_DELTAS = [33, 31, 18, 14, -14, -18, -31, -33] as const

/** Forward push direction, indexed by colour. */
export const PAWN_PUSH = [NORTH, SOUTH] as const
/** The two squares a pawn of this colour attacks, as deltas from the pawn. */
export const PAWN_CAPTURES = [
  [NORTH_EAST, NORTH_WEST],
  [SOUTH_EAST, SOUTH_WEST],
] as const
/** Rank a pawn starts on, and the rank it promotes upon reaching. */
export const PAWN_START_RANK = [1, 6] as const
export const PAWN_PROMOTION_RANK = [7, 0] as const

/**
 * `ALIGNMENT[0x77 + a - b]` is the queen-direction delta connecting two squares,
 * or 0 if they are unconnected. This works because in 0x88 the difference
 * between two aligned squares is always a small multiple of that direction, and
 * no two directions collide within a board's reach.
 */
const ALIGNMENT = ((): Int8Array => {
  const table = new Int8Array(240)
  for (const delta of QUEEN_DELTAS) {
    for (let distance = 1; distance <= 7; distance += 1) table[0x77 + delta * distance] = delta
  }
  return table
})()

export const alignmentDelta = (from: Square, to: Square): number =>
  ALIGNMENT[0x77 + from - to] as number

/** Same rank, file, or diagonal. Necessary (not sufficient) for a pin. */
export const isAligned = (from: Square, to: Square): boolean => alignmentDelta(from, to) !== 0

const rayHits = (
  board: Uint8Array,
  from: Square,
  deltas: readonly number[],
  first: PieceCode,
  second: PieceCode,
): boolean => {
  for (const delta of deltas) {
    let at = from + delta
    while (isOnBoard(at)) {
      const code = board[at] as PieceCode
      if (code !== EMPTY) {
        if (code === first || code === second) return true
        break
      }
      at += delta
    }
  }
  return false
}

/**
 * Is `target` attacked by any piece of colour `by`?
 *
 * Runs backwards from the target rather than forwards from every attacker,
 * which turns "who attacks this square" into a handful of short scans.
 */
export const isSquareAttacked = (pos: Position, target: Square, by: Color): boolean => {
  const { board } = pos

  // A white pawn attacking upward sits below-left or below-right of its target,
  // so we search along the *opposite* colour's capture directions.
  const pawn = encodePiece(by, PAWN)
  for (const delta of PAWN_CAPTURES[opposite(by)]) {
    const from = target + delta
    if (isOnBoard(from) && board[from] === pawn) return true
  }

  const knight = encodePiece(by, KNIGHT)
  for (const delta of KNIGHT_DELTAS) {
    const from = target + delta
    if (isOnBoard(from) && board[from] === knight) return true
  }

  const king = encodePiece(by, KING)
  for (const delta of KING_DELTAS) {
    const from = target + delta
    if (isOnBoard(from) && board[from] === king) return true
  }

  const queen = encodePiece(by, QUEEN)
  if (rayHits(board, target, BISHOP_DELTAS, encodePiece(by, BISHOP), queen)) return true
  if (rayHits(board, target, ROOK_DELTAS, encodePiece(by, ROOK), queen)) return true

  return false
}

export const isKingAttacked = (pos: Position, color: Color): boolean =>
  isSquareAttacked(pos, pos.kings[color], opposite(color))

/** Is the side to move in check? */
export const isInCheck = (pos: Position): boolean => isKingAttacked(pos, pos.turn)
